import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import userScope from "../CustomHooks/userScope";
import ApiSession from "../../api/ApiSession";
import Snackbar from "../SE-Component/Snackbar";
const apiSession = new ApiSession();
const InstallersTable = ({ dscDetails = [], onChangeInstallers, dscId }) => {
    const UserScope = userScope();
    const idPrefix = useMemo(
        () => `installer-${Math.random().toString(36).slice(2, 9)}`,
        []
    );

    const createInstallerId = () => {
        if (typeof globalThis !== "undefined" && globalThis?.crypto?.randomUUID) {
            return "new_" + globalThis.crypto.randomUUID();
        }
        return `new_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    };
    const [loading, setLoading] = useState(false);
    const incomingInstallers = useMemo(() => dscDetails ?? [], [dscDetails]);
    const [installers, setInstallers] = useState(incomingInstallers);
    const [installerData, setInstallerData] = useState({
        id: "",
        email: "",
        sendActivationId: false,
        sendRenewalReminders: false,
    });
    const [editingInstallerId, setEditingInstallerId] = useState("");
    const [deleteInstallerId, setDeleteInstallerId] = useState("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [snackBar, setSnackBar] = useState({ type: "", message: "" });
    
      const snackBarRef = useRef(null);
    useEffect(() => {
        setInstallers(incomingInstallers);
    }, [incomingInstallers]);

    const updateInstallers = (nextList) => {
        setInstallers(nextList);
        if (typeof onChangeInstallers === "function") {
            onChangeInstallers(nextList);
        }
    };
    const callSnackbar = useCallback((type, message) => {
        try {
          setSnackBar({ type, message });
          snackBarRef.current.open = true
        } catch (error) {
          console.log("error in snack bar")
        }
      }, []);
    const handleChange = (e) => {
        const { name, type, value, checked } = e.target;
        // Keep compatibility with existing input name="installerEmail"
        const fieldName =
            name === "installerEmail"
                ? "email"
                : name === "activationId"
                    ? "sendActivationId"
                    : name === "renewalRemainder"
                        ? "sendRenewalReminders"
                        : name;
        const fieldValue = type === "checkbox" ? checked : value;

        setInstallerData((prevState) => ({
            ...prevState,
            [fieldName]: fieldValue,
        }));
    };

    const openAddInstaller = () => {
        setInstallerData(prevState => ({
            ...prevState,
            id: "0",
        }));
    }

    const saveInstaller = () => {
        const email = String(installerData.email || "").trim();
        if (!email) return;

        const alreadyExists = installers.some(
            (i) => String(i?.email || "").trim().toLowerCase() === email.toLowerCase()
        );
        if (alreadyExists){
             callSnackbar("error", "Installer email is already exists");
             return;
            }

        const nextId = createInstallerId();
        const nextList = [
            ...installers,
            {
                id: nextId,
                email,
                sendActivationId: Boolean(installerData.sendActivationId),
                sendRenewalReminders: Boolean(installerData.sendRenewalReminders),
                dscId: dscId,
            },
        ];

        updateInstallers(nextList);
        cancelInstaller();
    };
    const updateInstaller = async () => {
        const isNewInstallerId = (id) => {
            const idStr = String(id ?? "");
            return idStr.startsWith("new_") || idStr.startsWith("new-");
        };

        const updatedList = installers.map((installer) => {
            if (isNewInstallerId(installer?.id)) {
                return {
                    ...installer,
                    id: 0,
                };
            }
            return installer;
        });

        console.log("Update installer clicked", updatedList);

        try {
            setLoading(true);
            const dscResp = await apiSession.updateDscInstallerEmail(updatedList);
            callSnackbar("success", "Installer emails updated successfully");

            const nextInstallers = Array.isArray(dscResp)
                ? dscResp
                : Array.isArray(dscResp?.data)
                    ? dscResp.data
                    : Array.isArray(dscResp?.installers)
                        ? dscResp.installers
                        : null;

            if (nextInstallers) {
                setLoading(false);
                updateInstallers(nextInstallers);
            }
        } catch (error) {
            setLoading(false);
            callSnackbar("error", "Installer emails update failed");
        }
    };

    const editInstaller = (installer) => {
        setEditingInstallerId(String(installer?.id ?? ""));
        setInstallerData({
            id: String(installer?.id ?? ""),
            email: String(installer?.email ?? ""),
            sendActivationId: Boolean(installer?.sendActivationId),
            sendRenewalReminders: Boolean(installer?.sendRenewalReminders),
        });
    };

    const resetEditInstaller = () => {
        setEditingInstallerId("");
        setInstallerData({
            id: "",
            email: "",
            sendActivationId: false,
            sendRenewalReminders: false,
        });
    };

    const installerUpdate = () => {
        const targetId = String(editingInstallerId || "");
        if (!targetId) return;

        const email = String(installerData.email || "").trim();
        if (!email) return;

        const emailExists = installers.some((i) => {
            const id = String(i?.id ?? "");
            if (id === targetId) return false;
            return String(i?.email || "").trim().toLowerCase() === email.toLowerCase();
        });
        if (emailExists) return;

        const nextList = installers.map((i) => {
            const id = String(i?.id ?? "");
            if (id !== targetId) return i;
            return {
                ...i,
                email,
                sendActivationId: Boolean(installerData.sendActivationId),
                sendRenewalReminders: Boolean(installerData.sendRenewalReminders),
            };
        });

        updateInstallers(nextList);
        resetEditInstaller();
    };

    const deleteInstallerPopUp = (installerId) => {
        setDeleteInstallerId(String(installerId ?? ""));
        setDeleteConfirmOpen(true);
    };

    const closeDeleteInstallerPopUp = () => {
        setDeleteInstallerId("");
        setDeleteConfirmOpen(false);
    };

    const removeInstallerConfirm = () => {
        if (!deleteInstallerId) {
            closeDeleteInstallerPopUp();
            return;
        }
        const nextList = installers.filter((i) => String(i?.id ?? "") !== deleteInstallerId);
        updateInstallers(nextList);
        if (editingInstallerId === deleteInstallerId) {
            resetEditInstaller();
        }
        closeDeleteInstallerPopUp();
    };
    const cancelInstaller = () => {
        setInstallerData({
            id: "",
            email: "",
            sendActivationId: false,
            sendRenewalReminders: false,
        });
    };
    return (
        <>
        {loading && <se-loading loading="true" full-page="true"></se-loading>}
            <div className="col-md-12 mt-2">
                <se-divider></se-divider>
            </div>
            <div className="col-sm-12 d-flex justify-content-end align-items-center mt-2 mb-2">

                {UserScope.access.dsc && installerData.id === "" && (
                   <>
                     {   <se-button
                        option="raised"
                        size="small"
                        color="primary"
                        icon="action_save"
                        onClick={updateInstaller}
                        style={{}}
                    >
                        Save
                    </se-button>}
                   <se-button
                        option="raised"
                        size="small"
                        color="primary"
                        icon="action_add_flat"
                        onClick={openAddInstaller}
                        style={{}}
                    >
                        Add Installer
                    </se-button>
                    </>
                )}
            </div>
            {installerData.id === "0" && (
                <div className="col-md-12 mt-2">
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group row">
                                <label
                                    htmlFor="inputEmail3"
                                    className="col-sm-12 col-form-label text-left"
                                >
                                    Installer Email<span className="se-mandatory">*</span>
                                </label>
                                <div className="col-sm-12">
                                    <input
                                        type="text"
                                        autoComplete="none"
                                        className="form-control form-control-sm"
                                        name="installerEmail"
                                        placeholder="Installer Email"
                                        value={installerData.email}
                                        maxLength="50"
                                        onChange={handleChange}
                                    />
                                    <div
                                        className="invalid-feedback-custom"
                                        style={!installerData.email ? {} : { display: "none" }}
                                    >
                                        Email id is required.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-group row align-items-center">
                                <label
                                    htmlFor="activationId"
                                    className="col-md-12 col-form-label"
                                >
                                    Send Activation Id
                                </label>
                                <div className="col-md-12">
                                    <div className="custom-control custom-checkbox">
                                        <input
                                            type="checkbox"
                                            className="custom-control-input asCompatible"
                                            id={`activationId-${idPrefix}`}
                                            name="activationId"
                                            checked={installerData.sendActivationId}
                                            onChange={handleChange}
                                        />
                                        <label
                                            className="custom-control-label"
                                            htmlFor={`activationId-${idPrefix}`}
                                        ></label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group row align-items-center">
                                <label
                                    htmlFor="renewalRemainder"
                                    className="col-md-12 col-form-label"
                                >
                                    Send Subscription Renewal Reminders
                                </label>
                                <div className="col-md-12">
                                    <div className="custom-control custom-checkbox">
                                        <input
                                            type="checkbox"
                                            className="custom-control-input asCompatible"
                                            id={`renewalRemainder-${idPrefix}`}
                                            name="renewalRemainder"
                                            checked={installerData.sendRenewalReminders}
                                            onChange={handleChange}
                                        />
                                        <label
                                            className="custom-control-label"
                                            htmlFor={`renewalRemainder-${idPrefix}`}
                                        ></label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-2 row align-items-center">
                            <span className="pointer">
                                <se-icon
                                    size="medium"
                                    color="primary"
                                    title="Click to Save"
                                    disabled={
                                        installerData.email.trim() === ""
                                            ? true
                                            : false
                                    }
                                    onClick={
                                        installerData.email.trim() === ""
                                            ? undefined
                                            : saveInstaller
                                    }
                                >
                                    notification_ok_stroke
                                </se-icon>
                            </span>
                            <span>&nbsp;</span>
                            <span className="pointer">
                                {" "}
                                <se-icon
                                    size="medium"
                                    title="Click to cancel"
                                    onClick={cancelInstaller}
                                >
                                    action_delete_cross
                                </se-icon>
                            </span>
                        </div>


                    </div>
                </div>
            )}
            {installers.length > 0 && <div className="col-sm-12 mb-2">
                <table>
                    <thead>
                        <tr>
                            <th>Installer Email</th>
                            <th>Send Activation ID</th>
                            <th>Send Subscription Renewal Reminders</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {installers.map((installer) => {
                            const rowId = String(installer?.id ?? "");
                            const isEditing = rowId && rowId === String(editingInstallerId);
                            return (
                                <tr key={rowId || installer.email}>
                                    <td>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                autoComplete="none"
                                                className="form-control form-control-sm"
                                                name="installerEmail"
                                                value={installerData.email}
                                                maxLength="50"
                                                onChange={handleChange}
                                            />
                                        ) : (
                                            installer.email
                                        )}
                                    </td>
                                    <td>
                                        {isEditing ? (
                                            <div className="custom-control custom-checkbox">
                                                <input
                                                    id={`${rowId || installer.email}+ 1`}
                                                    type="checkbox"
                                                    className="custom-control-input asCompatible"
                                                    checked={installerData.sendActivationId}
                                                    name="activationId"
                                                    onChange={handleChange}
                                                />
                                                <label
                                                    className="custom-control-label"
                                                    htmlFor={`${rowId || installer.email}+ 1`}
                                                ></label>
                                            </div>
                                        ) : (
                                            <div className="custom-control custom-checkbox">
                                                <input
                                                    id={`${rowId || installer.email}+ 1`}
                                                    type="checkbox"
                                                    className="custom-control-input asCompatible"
                                                    checked={Boolean(installer.sendActivationId)}
                                                    readOnly
                                                    disabled
                                                />
                                                <label
                                                    className="custom-control-label"
                                                    htmlFor={`${rowId || installer.email}+ 1`}
                                                ></label>
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {isEditing ? (

                                            <div className="custom-control custom-checkbox">
                                                <input
                                                    id={`${rowId || installer.email}+ 2`}
                                                    type="checkbox"
                                                    className="custom-control-input asCompatible"
                                                    checked={installerData.sendRenewalReminders}
                                                    name="renewalRemainder"
                                                    onChange={handleChange}
                                                />
                                                <label
                                                    className="custom-control-label"
                                                    htmlFor={`${rowId || installer.email}+ 2`}
                                                ></label>
                                            </div>
                                        ) : (
                                            <div className="custom-control custom-checkbox">
                                                <input
                                                    id={`${rowId || installer.email}+ 2`}
                                                    type="checkbox"
                                                    className="custom-control-input asCompatible"
                                                    checked={Boolean(installer.sendRenewalReminders)}
                                                    disabled
                                                    readOnly
                                                />
                                                <label
                                                    className="custom-control-label"
                                                    htmlFor={`${rowId || installer.email}+ 2`}
                                                ></label>
                                            </div>
                                        )}
                                    </td>
                                    <td className="align-middle text-center">
                                        {isEditing ? (
                                            <>
                                                <span className="pointer">
                                                    <se-icon
                                                        size="small"
                                                        color="primary"
                                                        title="Click to update"
                                                        disabled={String(installerData.email || "").trim() === ""}
                                                        onClick={
                                                            String(installerData.email || "").trim() === ""
                                                                ? undefined
                                                                : installerUpdate
                                                        }
                                                    >
                                                        notification_ok_stroke
                                                    </se-icon>
                                                </span>
                                                <span>&nbsp;</span>
                                                <span className="pointer">
                                                    <se-icon
                                                        size="small"
                                                        title="Click to cancel"
                                                        onClick={resetEditInstaller}
                                                    >
                                                        action_delete_cross
                                                    </se-icon>
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                {UserScope.access.dsc === true ? (
                                                    <span className="pointer">
                                                        <se-icon
                                                            size="small"
                                                            title="Edit"
                                                            color="primary"
                                                            onClick={() => editInstaller(installer)}
                                                        >
                                                            action_editor
                                                        </se-icon>
                                                    </span>
                                                ) : (
                                                    ""
                                                )}
                                                <span>&nbsp; &nbsp;</span>
                                                {UserScope.access.dsc === true ? (
                                                    <span className="pointer">
                                                        <se-icon
                                                            size="small"
                                                            title="Delete"
                                                            color="primary"
                                                            onClick={() => deleteInstallerPopUp(installer.id)}
                                                        >
                                                            action_delete
                                                        </se-icon>
                                                    </span>
                                                ) : (
                                                    ""
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>}
             <Snackbar snackBar={snackBar} ref={snackBarRef} />
            <se-dialog
                id="dialog-installers-delete-confirm"
                open={deleteConfirmOpen}
                can-backdrop="false"
            >
                <se-dialog-header>Confirmation</se-dialog-header>
                <se-dialog-content>
                    <span className="se-font-14px">
                        Do you confirm to delete this installer?
                    </span>
                </se-dialog-content>
                <se-dialog-footer>
                    <se-button onClick={closeDeleteInstallerPopUp}>Cancel</se-button>
                    <se-button onClick={removeInstallerConfirm}>OK</se-button>
                </se-dialog-footer>
            </se-dialog>
        </>
    );
}
InstallersTable.propTypes = {
    dscDetails: PropTypes.shape({
        installers: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                email: PropTypes.string.isRequired,
                sendActivationId: PropTypes.bool,
                sendRenewalReminders: PropTypes.bool,
            })
        ),
    }),
    onChangeInstallers: PropTypes.func,
};
export default InstallersTable;
